import React, { useState, useEffect, useRef } from "react";
import {
  UserOutlined,
  LoginOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import {
  Breadcrumb,
  Flex,
  Layout,
  Menu,
  theme,
  Button,
  Avatar,
  Typography,
  Space,
} from "antd";
import useBreadcrumbs from "use-react-router-breadcrumbs";
import { Outlet, useNavigate, useOutletContext } from "react-router-dom";
import type { User } from "../code/utils";
import { config } from "../code/vars";
import { supabase, signOut } from "../lib/supabase";
import type { User as SupabaseUser } from "@supabase/supabase-js";

const { Header, Content, Footer } = Layout;
const { Title } = Typography;

type ContextType = { user: SupabaseUser | null; profile: { activated: boolean } | null };

type MenuItem = Required<MenuProps>["items"][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[]
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
  } as MenuItem;
}

const Breadcrumbs = () => {
  const breadcrumbs = useBreadcrumbs();

  return (
    <Breadcrumb style={{ margin: "16px 0" }}>
      {breadcrumbs.map(({ breadcrumb }) => (
        <Breadcrumb.Item key={breadcrumb?.toString()}>
          {breadcrumb}
        </Breadcrumb.Item>
      ))}
    </Breadcrumb>
  );
};

const App: React.FC = () => {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [profile, setProfile] = useState<{ activated: boolean } | null>(null);
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id);
      } else {
        setUser(null);
        setProfile(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  async function fetchProfile(userId: string) {
    const { data } = await supabase
      .from('profiles')
      .select('activated')
      .eq('id', userId)
      .single();
    
    if (data) {
      setProfile(data);
    }
  }

  async function handleSignOut() {
    await signOut();
    navigate('/signin');
  }

  const menuItems = [
    {
      key: "discover",
      label: "Discover",
      onClick: () => {
        navigate("/");
      },
    },
    {
      key: "create",
      label: "Industries",
      onClick: () => {
        navigate("/create/");
      },
    }
  ];

  // useEffect(()=>{
  //   getProfile();
  // }, [])

  return (
    <Layout style={{ minHeight: "100vh", background: "#FbF3F2" }}>
      <Header
        style={{
          background: "#FbF3F2",
          padding: "0 24px",
          height: "64px",
          lineHeight: "64px",
          position: "sticky",
          top: 0,
          zIndex: 1000,
        }}
      >
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "200px 1fr 200px", 
          alignItems: "center", 
          height: "100%",
          width: "100%"
        }}>
          {/* Logo on the left */}
          <div>
            <Title
              level={3}
              style={{ 
                color: "#032539", 
                margin: 0, 
                fontWeight: "bold",
                fontSize: "24px",
                cursor: "pointer"
              }}
              onClick={() => navigate("/")}
            >
              Twinfinity
            </Title>
          </div>

          {/* Menu in the center */}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Menu
              mode="horizontal"
              defaultSelectedKeys={["discover"]}
              items={menuItems}
              style={{ 
                background: "transparent",
                border: "none",
                minWidth: 0
              }}
            />
          </div>

          {/* Sign In / Get Started on the right */}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Space>
              {user ? (
                <Space>
                  <Avatar 
                    style={{ backgroundColor: "#1C768F" }} 
                    size="small" 
                    src={user.user_metadata?.avatar_url}
                    icon={<UserOutlined />} 
                  />
                  <Button 
                    type="primary"
                    onClick={handleSignOut}
                    icon={<LogoutOutlined />}
                    style={{ 
                      background: "#032539",
                      borderColor: "#032539",
                      color: "#FbF3F2"
                    }}
                  >
                    Sign Out
                  </Button>
                </Space>
              ) : (
                <Space>
                  <Button 
                    type="default"
                    onClick={() => navigate("/signin")}
                    style={{ 
                      background: "transparent",
                      borderColor: "transparent",
                      color: "#032539"
                    }}
                  >
                    Sign In
                  </Button>
                  <Button 
                    type="primary"
                    onClick={() => navigate("/signin")}
                    style={{ 
                      background: "rgba(3, 37, 57, 0.1)",
                      borderColor: "transparent",
                      color: "#000000",
                      boxShadow: "none"
                    }}
                  >
                    Get Started
                  </Button>
                </Space>
              )}
            </Space>
          </div>
        </div>
      </Header>
      
      <Content
        style={{ 
          padding: "24px",
          background: "#FbF3F2",
          minHeight: "calc(100vh - 64px)"
        }}
      >
        <div style={{ 
          width: "100%", 
          height: "100%",
          maxWidth: "1200px",
          margin: "0 auto"
        }} ref={containerRef}>
          <Outlet context={{ user, profile }} />
        </div>
      </Content>
    </Layout>
  );
};

export function useUser() {
  return useOutletContext<ContextType>();
}

export default App;
